import React, {useState} from 'react';
import {Box, Typography, Grid, Tabs, Tab, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import {PageHeader} from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {CONTEXT_BAND_COLORS} from '../constants';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem, isSuppressedKpi, isSuppressedBenchmark, isSuppressedCell, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

import {BandChip as SharedBandChip, GuidanceBanner} from '../../DashboardCommon/widgets.comp';
import {fmtValue, benchmarkText} from '../../DashboardCommon/units';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortItems, sortBars} from '../../DashboardCommon/barSort';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';
// Secondary benchmark-marker colour (Global) — kept distinct from NAVY and
// from every band fill colour used on the bars themselves.
const MARKER_SECONDARY = 'var(--brand-global-2, #7B8FC7)';

/* ── Band chip (matches School Dashboard's BandChip pill) ─────────── */
const contextBandColor = (band) => CONTEXT_BAND_COLORS[band] || '#6B7280';

const BandChip = ({label, color}) => <SharedBandChip label={label} color={color || contextBandColor(label)}/>;

/* ── Shared progress bar — score fill + single Global benchmark marker.
   National has no benchmark above itself (unlike School, which shows both
   National + Global markers against its own score), so only one marker. ── */
const ScoreBar = ({score, band, benchmark, height = '12px', radius = '6px'}) => (
    <Box sx={{height, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: radius, position: 'relative', flex: 1}}>
        <Tooltip title={fmtValue(score)} arrow placement="top">
            <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${score ?? 0}%`, backgroundColor: contextBandColor(band), borderRadius: radius, cursor: 'pointer',
            }}/>
        </Tooltip>
        {benchmark != null && (
            <Tooltip title={benchmarkText('Global avg', benchmark)} arrow placement="bottom">
                <Box sx={{
                    position: 'absolute', left: `${benchmark}%`, bottom: '-9px', width: 0, height: 0,
                    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                    borderBottom: `9px solid ${MARKER_SECONDARY}`,
                    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
                }}/>
            </Tooltip>
        )}
    </Box>
);

// Rubric band colored chips (Strong/Adequate/Limited/Low) + Global avg
// marker — replaces the earlier plain "National score" color swatch per
// the deck's own "Replace this with rubric band colored chips." annotation.
const BarLegend = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
        {Object.keys(CONTEXT_BAND_COLORS).map((band) => (
            <BandChip key={band} label={band} color={CONTEXT_BAND_COLORS[band]}/>
        ))}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box sx={{width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `7px solid ${MARKER_SECONDARY}`}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Global avg</Typography>
        </Box>
    </Box>
);

/* ── KPI stat card ──────────────────────────────────────────────── */
// A KPI under the n floor (suppressed) reads "—" with the reason underneath and no band.
const StatCard = ({label, score, band, suppressed}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    }}>
        <Box sx={{backgroundColor: suppressed ? '#D1D5DB' : contextBandColor(band), height: '6px', flexShrink: 0}}/>
        <Box sx={{p: 2.5, display: 'flex', flexDirection: 'column', flex: 1}}>
            <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '1px', textTransform: 'uppercase', mb: 1}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: {xs: '32px', md: '40px'}, fontWeight: 800, color: NAVY, lineHeight: 1}}>
                {suppressed || score === null ? '—' : score}
            </Typography>
            {suppressed && <Typography sx={{fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', mt: 0.75}}>{SUPPRESSED_COPY}</Typography>}
            {!suppressed && band && (
                <Box sx={{mt: 'auto', pt: 1.5}}>
                    <BandChip label={band}/>
                </Box>
            )}
        </Box>
    </Box>
);

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 1 — OVERVIEW                                               */
/* ════════════════════════════════════════════════════════════════ */

// An area under the n floor (suppressed) shows the placeholder in place of its bar, score and band.
const AreaBar = ({area, name, score, band, benchmark, suppressed}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
        <Box sx={{width: {xs: 130, md: 220}, flexShrink: 0}}>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>{area}</Typography>
            <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY}}>{name}</Typography>
        </Box>
        {suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (<>
        <ScoreBar score={score} band={band} benchmark={benchmark} height="16px" radius="8px"/>
        <Typography sx={{fontSize: '16px', fontWeight: 800, color: NAVY, width: 34, textAlign: 'right', flexShrink: 0}}>{score ?? '—'}</Typography>
        <Box sx={{flexShrink: 0}}>{band && <BandChip label={band}/>}</Box>
        </>)}
    </Box>
);

const OverviewTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_8a' });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    // Defensively locate section S_8a
    let s8aSection = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_8a') {
            s8aSection = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s8aSection = dashboardData.cards.sections.find(s => s.section_code === 'S_8a');
        } else if (Array.isArray(dashboardData.sections)) {
            s8aSection = dashboardData.sections.find(s => s.section_code === 'S_8a');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s8aSection = dashboardData;
        }
    }

    if (loading) {
        return <CircularLoader minHeight="300px" />;
    }

    if (!dashboardData || !s8aSection) {
        return <NoDataAvailable />;
    }

    const cards = s8aSection?.cards || [];

    const nationalAvgCard = cards.find(c => c.card_type === 'KPICard' && c.kpi_name?.toUpperCase()?.includes('NATIONAL'));
    const globalAvgCard = cards.find(c => c.card_type === 'KPICard' && c.kpi_name?.toUpperCase()?.includes('GLOBAL'));

    const nationalSuppressed = isSuppressedKpi(nationalAvgCard);        // under the n floor: "—" and the reason
    const nationalAvg = !nationalSuppressed && nationalAvgCard?.kpi_value != null ? Math.round(nationalAvgCard.kpi_value) : null;
    const nationalBand = nationalSuppressed ? '' : (nationalAvgCard?.kpi_band || '');

    const globalAvg = globalAvgCard?.kpi_value != null ? Math.round(globalAvgCard.kpi_value) : null;
    const globalBand = globalAvgCard?.kpi_band || '';

    const barChartCard = cards.find(c => c.card_type === 'BarChartBMCard');
    const items = barChartCard?.items || [];

    const areaScores = items.map((item, index) => {
        const areaNum = item.sublabel ? (item.sublabel.match(/\d+/)?.[0] || (index + 1)) : (index + 1);
        const bench = item.benchmark_values?.find(b => b.name?.includes('Global')) ?? item.benchmark_values?.[0];
        const benchmarkVal = bench && !isSuppressedBenchmark(bench) ? bench.value : null;
        const suppressed = isSuppressedItem(item);                 // under the n floor: placeholder row
        return {
            area: `Context Area ${areaNum}`,
            name: item.label,
            suppressed,
            score: !suppressed && item.value != null ? Math.round(item.value) : null,
            band: suppressed ? '' : (item.band || ''),
            benchmark: benchmarkVal != null ? Math.round(benchmarkVal) : null
        };
    });
    // Group order is the card's area order; the value modes rank the areas (suppressed ones last).
    const shownAreas = sortItems(areaScores, sortMode);

    const leftLineCard = cards.find(c => c.card_type === 'LeftLineCard');
    // The lowest area is picked among the areas that show a score.
    const scoredAreas = areaScores.filter((a) => a.score !== null);
    const lowestArea = scoredAreas.length > 0
        ? scoredAreas.reduce((min, a) => a.score < min.score ? a : min, scoredAreas[0])
        : null;

    const lowestAreaName = lowestArea?.name || lowestArea?.area || '';
    let guidanceHtml = leftLineCard?.leftline_html || '';
    if (guidanceHtml && lowestAreaName) {
        guidanceHtml = guidanceHtml
            .replace(/\{Lowest Area\}/g, lowestAreaName)
    }

    return (
        <Box>
            <PageHeader
                title="Context & Support Overview"
                subtitle="What conditions may explain differences and guide support?"
            />

            {(nationalAvgCard || globalAvgCard) && (
                <Grid container spacing={2.5} sx={{mb: 3}}>
                    {nationalAvgCard && (
                        <Grid item xs={12} md={globalAvgCard ? 6 : 12}>
                            <StatCard label={nationalAvgCard.kpi_name || "National Average"} score={nationalAvg} band={nationalBand} suppressed={nationalSuppressed}/>
                        </Grid>
                    )}
                    {globalAvgCard && (
                        <Grid item xs={12} md={nationalAvgCard ? 6 : 12}>
                            <StatCard label={globalAvgCard.kpi_name || "Global Average"} score={globalAvg} band={globalBand}/>
                        </Grid>
                    )}
                </Grid>
            )}

            {areaScores.length > 0 && (
                <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                            {barChartCard?.title || ""}
                        </Typography>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                            <BarLegend/>
                            <SortToggle value={sortMode} onChange={setSortMode}/>
                        </Box>
                    </Box>
                    {shownAreas.map((a) => <AreaBar key={a.area} {...a}/>)}
                </Box>
            )}

            {/* Decision guidance (hidden) */}
            {/* {guidanceHtml && (
                <Box
                    sx={{
                        mt: 3, border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px',
                        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: 2.5,
                        '& p': { m: 0, fontSize: '13px', color: '#374151', lineHeight: 1.6, mb: 1 },
                        '& p:last-child': { mb: 0 },
                        '& ul': { m: 0, pl: 2.5 },
                        '& li': { mb: 0.8, fontSize: '13px', color: '#374151', lineHeight: 1.6 },
                        '& strong': { color: NAVY, fontWeight: 700 }
                    }}
                    dangerouslySetInnerHTML={{ __html: guidanceHtml }}
                />
            )} */}
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 2 — ITEM PROFILE                                           */
/* ════════════════════════════════════════════════════════════════ */

// An item under the n floor (suppressed) shows the placeholder in place of its bar, score and band.
const ItemRow = ({itemLabel, label, score, band, benchmark, suppressed}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2.2, pl: 4}}>
        <Box sx={{width: {xs: 160, md: 220}, flexShrink: 0}}>
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.3}}>{itemLabel}</Typography>
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{label}</Typography>
        </Box>
        {suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (<>
        <ScoreBar score={score} band={band} benchmark={benchmark} height="12px" radius="6px"/>
        <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY, width: 30, textAlign: 'right', flexShrink: 0}}>{score ?? '—'}</Typography>
        <Box sx={{flexShrink: 0}}>{band && <BandChip label={band}/>}</Box>
        </>)}
    </Box>
);

const ListCard = ({title, items, color}) => (
    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%'}}>
        <Box sx={{backgroundColor: color, height: '6px'}}/>
        <Box sx={{p: 3}}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>{title}</Typography>
            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                {items.map((it) => (
                    <Box component="li" key={it} sx={{mb: 1}}>
                        <Typography sx={{fontSize: '13.5px', color: '#374151', lineHeight: 1.5}}>{it}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const ItemProfileTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_8b' });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    // Defensively locate section S_6b
    let s8bSection = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_8b') {
            s8bSection = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s8bSection = dashboardData.cards.sections.find(s => s.section_code === 'S_8b');
        } else if (Array.isArray(dashboardData.sections)) {
            s8bSection = dashboardData.sections.find(s => s.section_code === 'S_8b');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s8bSection = dashboardData;
        }
    }

    if (loading) {
        return <CircularLoader minHeight="300px" />;
    }

    if (!dashboardData || !s8bSection) {
        return <NoDataAvailable />;
    }

    const cards = s8bSection?.cards || [];

    const barChartCard = cards.find(c => c.card_type === 'BarChartBMCard');
    const items = barChartCard?.items || [];

    const dynamicItems = items.map((item, index) => {
        const bench = item.benchmark_values?.find(b => b.name?.includes('Global')) ?? item.benchmark_values?.[0];
        const benchmarkVal = bench && !isSuppressedBenchmark(bench) ? bench.value : null;
        const suppressed = isSuppressedItem(item);                 // under the n floor: placeholder row
        const areaNum = item.parent_sublabel ? (item.parent_sublabel.match(/\d+/)?.[0] || '1') : '1';

        return {
            code: item.code || item.sublabel || `CTX ${String(index + 1).padStart(2, '0')}`,
            itemLabel: '',                       // numbered below, 1…N straight through the areas (QA 9 Oct)
            areaTitle: `Context Area ${areaNum}`,
            parentLabel: item.parent_label || 'Context Area',
            label: item.label,
            suppressed,
            score: !suppressed && item.value != null ? Math.round(item.value) : null,
            band: suppressed ? '' : (item.band || ''),
            benchmark: benchmarkVal != null ? Math.round(benchmarkVal) : null
        };
    });

    const areasMap = new Map();
    dynamicItems.forEach((it) => {
        const groupKey = it.parentLabel;
        if (!areasMap.has(groupKey)) {
            areasMap.set(groupKey, {areaTitle: it.areaTitle, parentLabel: it.parentLabel, items: []});
        }
        areasMap.get(groupKey).items.push(it);
    });
    const groupedAreas = Array.from(areasMap.values());
    // Items are numbered in the order shown, continuing across areas ("Context Item 1 … 8"), whatever the card's own sublabel says.
    let itemNo = 0;
    groupedAreas.forEach((g) => g.items.forEach((it) => { itemNo += 1; it.itemLabel = `Context Item ${itemNo}`; }));
    const shownGroups = sortBars(groupedAreas, sortMode);

    // Rankings run over the items that show a score; when none does, the lists say why.
    const scoredItems = dynamicItems.filter((it) => it.score !== null);
    const noneShown = dynamicItems.length > 0 && scoredItems.length === 0 ? [SUPPRESSED_COPY] : [];
    const sortedItems = [...scoredItems].sort((a, b) => b.score - a.score);
    // Ascending by rounded score (stable sort, so ties keep card order) — shared by
    // Needs Attention and the guidance sentence so both name the same lowest item.
    const ascItems = [...scoredItems].sort((a, b) => a.score - b.score);
    const strengths = sortedItems.length ? sortedItems.slice(0, 3).map(it => `${it.label} (${it.score})`) : noneShown;
    const needs = sortedItems.length ? ascItems.slice(0, 3).map(it => `${it.label} (${it.score})`) : noneShown;

    const lowestItem = ascItems[0];

    // The deck's subtitle ("Which readiness items need national attention?") is a copy-paste leftover from the Readiness template; it is worded here for this page's topic.
    return (
        <Box>
            <PageHeader
                title="Context & Support Item Profile"
                subtitle="Which context & support items need national attention?"
            />

            <Grid container spacing={2.5} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                                {barChartCard?.title || ""}
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                <BarLegend/>
                                <SortToggle value={sortMode} onChange={setSortMode}/>
                            </Box>
                        </Box>
                        {shownGroups.map((areaGroup, idx) => (
                            <Box key={areaGroup.parentLabel || 'all'} sx={{mb: idx < shownGroups.length - 1 ? 3.5 : 0}}>
                                {(areaGroup.areaTitle || areaGroup.parentLabel) && <Box sx={{mb: 2}}>
                                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.3}}>
                                        {areaGroup.areaTitle}
                                    </Typography>
                                    <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>
                                        {areaGroup.parentLabel}
                                    </Typography>
                                </Box>}
                                {areaGroup.items.map((it) => (
                                    <ItemRow key={it.code} {...it} itemLabel={areaGroup.flattened && it.groupLabel ? `${it.itemLabel} · ${it.groupLabel}` : it.itemLabel}/>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <ListCard title="Strengths" items={strengths} color="#1B6B55"/>
                    <Box sx={{mt: 2.5}}>
                        <ListCard title="Needs Attention" items={needs} color="#E04535"/>
                    </Box>
                    {lowestItem && (
                        <GuidanceBanner
                            label="Recommended action:"
                            text={`"${lowestItem.label}" (${lowestItem.score}) is the lowest item. Start with one focused Area on it, then re-check the regional heatmap to target support where the gap is widest.`}
                        />
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  TAB 3 — BY REGION                                              */
/* ════════════════════════════════════════════════════════════════ */

const getBandColor = (band) => {
    if (!band || typeof band !== 'string') return null;
    const lowerBand = band.toLowerCase();
    const matchedKey = Object.keys(CONTEXT_BAND_COLORS).find((key) => key.toLowerCase() === lowerBand);
    return matchedKey ? CONTEXT_BAND_COLORS[matchedKey] : null;
};

const scoreToBand = (v) => {
    const val = v || 0;
    if (val >= 75) return 'Strong';
    if (val >= 50) return 'Adequate';
    if (val >= 25) return 'Limited';
    return 'Low';
};

const getCellBg = (band, val) => {
    // No band and no value (a cell that is not sent) stays uncoloured rather than reading as "Low".
    const color = getBandColor(band) || (val === null || val === undefined ? null : CONTEXT_BAND_COLORS[scoreToBand(val)]);
    if (!color) return 'transparent';
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const opacity = color.toUpperCase() === '#E04535' ? 0.92 : 0.8;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const heatCell = {textAlign: 'center', py: 1.6, px: 2, borderBottom: '1px solid #F3F4F6', fontSize: '14px', fontWeight: 700, color: NAVY};
const heatHeadCell = {backgroundColor: NAVY, color: '#fff', fontSize: '13px', fontWeight: 700, borderBottom: 'none', py: 1.75, px: 2};

// cell_data carries {band, n, suppressed}; a region under the n floor shows the placeholder, uncoloured.
const heatCellOf = (col, ri) => {
    const cd = col.bands?.[ri];
    const sup = !!cd && typeof cd === 'object' && isSuppressedCell(cd);
    const band = typeof cd === 'string' ? cd : cd?.band;
    return {sup, band, val: sup ? null : col.values[ri]};
};

// The heat-map sits in its own component so the sort/filter hook runs below the tab's loading
// branches (QA 9 Oct 2026). Rows are the regions; each context-area column reads its score
// through an accessor — null under the n floor, so such cells sort last and drop out of a range filter.
const RegionHeatmapTable = ({regions, contextCols}) => {
    const rows = React.useMemo(() => regions.map((region, ri) => ({region, ri})), [regions]);
    const columns = React.useMemo(() => [
        {key: 'region', label: 'Region', type: 'text'},
        ...contextCols.map((col) => ({key: col.label, label: col.label, type: 'number', accessor: (r) => heatCellOf(col, r.ri).val})),
    ], [contextCols]);
    const t = useTableControls(rows, columns);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
                <Table sx={{minWidth: 650}}>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('region')} sx={{...heatHeadCell, textAlign: 'left'}}/>
                            {contextCols.map((col) => (
                                <SortFilterHeaderCell key={col.label} {...t.headerProps(col.label)} sx={{...heatHeadCell, textAlign: 'center'}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={1 + contextCols.length} sx={{...heatCell, fontWeight: 400, fontSize: '13px', color: '#6A7282', py: 3}}>No region matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map(({region, ri}) => (
                            <TableRow key={region}>
                                <TableCell sx={{...heatCell, textAlign: 'left', backgroundColor: 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                    {region}
                                </TableCell>
                                {contextCols.map((col) => {
                                    const {sup, band, val} = heatCellOf(col, ri);
                                    return (
                                        <TableCell key={col.label} sx={{...heatCell, backgroundColor: sup ? 'transparent' : getCellBg(band, val)}}>
                                            {sup ? <SuppressedValue variant="cell"/> : (val != null ? Math.round(val) : '—')}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const ByRegionTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: 'S_8c' });

    // Defensively locate section S_6c
    let s8cSection = null;
    if (dashboardData) {
        if (dashboardData.section_code === 'S_8c') {
            s8cSection = dashboardData;
        } else if (dashboardData.cards?.sections) {
            s8cSection = dashboardData.cards.sections.find(s => s.section_code === 'S_8c');
        } else if (Array.isArray(dashboardData.sections)) {
            s8cSection = dashboardData.sections.find(s => s.section_code === 'S_8c');
        } else if (dashboardData.cards && Array.isArray(dashboardData.cards)) {
            s8cSection = dashboardData;
        }
    }

    if (loading) {
        return <CircularLoader minHeight="300px" />;
    }

    if (!dashboardData || !s8cSection) {
        return <NoDataAvailable />;
    }

    const cards = s8cSection?.cards || [];

    const tableCard = cards.find(c => c.card_type === 'TableRubricCard');
    const tableObjects = tableCard?.table_objects || [];

    const regionCol = tableObjects.find(c => c.column_type === 'text');
    const regions = regionCol?.values || [];
    const rubricCols = tableObjects.filter(c => c.column_type === 'rubric');

    // Column headers use a short "Context Area N" label; the full descriptive
    // name goes in the legend below the table instead of wrapping the header.
    const contextCols = rubricCols.map((col) => {
        const isComposite = col.column_name === 'Composite';
        let label = col.column_name;
        if (!isComposite) {
            const subname = (col.column_subname !== undefined && col.column_subname !== null) ? String(col.column_subname).trim() : '';
            const numMatch = subname.match(/\d+/);
            label = numMatch ? `Context Area ${numMatch[0]}` : col.column_name;
        }
        return {
            label,
            name: col.column_name,
            isComposite,
            values: col.values || [],
            bands: col.bands || col.cell_data || []
        };
    });

    const contextAreaLegend = contextCols.filter(c => !c.isComposite);

    // Lowest region by the Composite column, among regions whose cell shows a score
    // (suppressed cells and missing values take no part); ties keep table order.
    // Needs at least two scored regions, otherwise the guidance is hidden.
    const compositeCol = contextCols.find(c => c.isComposite);
    const scoredRegions = compositeCol
        ? regions
            .map((region, ri) => ({region, ...heatCellOf(compositeCol, ri)}))
            .filter(r => !r.sup && r.val != null)
            .map(r => ({region: r.region, score: Math.round(r.val)}))
        : [];
    const lowestRegion = scoredRegions.length >= 2
        ? scoredRegions.reduce((min, r) => (r.score < min.score ? r : min), scoredRegions[0])
        : null;

    return (
        <Box sx={{maxWidth: '100%', overflow: 'hidden'}}>
            <PageHeader
                title="Context & Support by Region"
                subtitle="Which regions need the most support, and in what area?"
            />

            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1}}>
                <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>
                    {tableCard?.title || ""}
                </Typography>
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                    {Object.keys(CONTEXT_BAND_COLORS).map((band) => <BandChip key={band} label={band}/>)}
                </Box>
            </Box>

            <Box sx={{maxWidth: '100%', overflow: 'hidden'}}>
                <RegionHeatmapTable regions={regions} contextCols={contextCols}/>
            </Box>

            {contextAreaLegend.length > 0 && (
                <Box sx={{mt: 3.5, mb: 1}}>
                    <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 1.5}}>
                        List of the Context &amp; Support Areas
                    </Typography>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, pl: 2}}>
                        {contextAreaLegend.map((c) => (
                            <Typography key={c.label} sx={{fontSize: '14px', color: '#1F2937'}}>
                                <strong style={{fontWeight: 700, color: '#000000'}}>{c.label}:</strong> {c.name}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}

            {lowestRegion && (
                <GuidanceBanner
                    label="Recommended action:"
                    text={`Patterns across regions guide where to focus. ${lowestRegion.region} is the lowest region average; check its coverage before acting.`}
                />
            )}
        </Box>
    );
};

/* ════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                  */
/* ════════════════════════════════════════════════════════════════ */

const ContextEquitySection = () => {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <NationalDashboardShell
            tabs={
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
                    sx={{
                        borderBottom: '1px solid #E5E7EB',
                        mb: 1,
                        '& .MuiTab-root': {textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2},
                        '& .Mui-selected': {color: `${ORANGE} !important`},
                    }}
                >
                    <Tab label="Overview"/>
                    <Tab label="Item Profile"/>
                    <Tab label="By Region"/>
                </Tabs>
            }
        >
            {activeTab === 0 && <OverviewTab/>}
            {activeTab === 1 && <ItemProfileTab/>}
            {activeTab === 2 && <ByRegionTab/>}
        </NationalDashboardShell>
    );
};

export {OverviewTab, ItemProfileTab, ByRegionTab};
export default ContextEquitySection;
